import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";

// ── Shared layout helpers ──────────────────────────────────────────────────
const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 26,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number; color?: string }> = ({
  children,
  size = 88,
  color = COLORS.text,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/** Fade+rise a block in over [a,b] (scene-local frames). */
const useReveal = (a: number, b: number) =>
  interpolate(useCurrentFrame(), [a, b], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

const LayerTag: React.FC<{ n: number; color: string }> = ({ n, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 16,
      letterSpacing: "0.28em",
      color: COLORS.dim,
      marginBottom: 10,
    }}
  >
    LAYER <span style={{ color }}>{n}</span> / 6
  </div>
);

// ── 01 · HOOK ───────────────────────────────────────────────────────────────
// Confident fabricated email → hard cut to the gate BLOCKING it in <150ms.
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  // The "email" composes early; the gate slams down at ~frame 300 (10s in).
  const emailIn = useReveal(20, 70);
  const cut = frame >= 300; // hard cut to the blocked state
  const blockIn = interpolate(frame, [300, 320], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const stampScale = interpolate(frame, [300, 330], [1.25, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const ms = Math.round(interpolate(frame, [320, 360], [0, 147], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  }));
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      {!cut ? (
        <AbsoluteFill style={{ ...center, opacity: emailIn }}>
          <Kicker text="production postmortem · 03:14" color={COLORS.red} />
          <Panel accent="red" title="outbound · agent → customer" width={1180}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 26, lineHeight: 1.85, color: COLORS.text }}>
              <div style={{ color: COLORS.dim }}>To: customer@acme.com</div>
              <div style={{ color: COLORS.dim, marginBottom: 14 }}>Subject: Re: the rollout fix</div>
              <div>…the change landed in{" "}
                <span style={{ color: COLORS.red }}>docs/fake.md</span>, see commit{" "}
                <span style={{ color: COLORS.red }}>b6d12b79</span>.</div>
              <div style={{ color: COLORS.dim, marginTop: 14 }}>
                ✓ fluent · ✓ cited · <span style={{ color: COLORS.red }}>✗ both invented</span>
              </div>
            </div>
          </Panel>
        </AbsoluteFill>
      ) : (
        <AbsoluteFill style={{ ...center, opacity: blockIn }}>
          <Kicker text="verify_output · deterministic gate" color={COLORS.amber} />
          <div
            style={{
              transform: `scale(${stampScale})`,
              border: `3px solid ${COLORS.red}`,
              borderRadius: 14,
              padding: "26px 60px",
              boxShadow: `0 0 60px -10px ${COLORS.red}`,
            }}
          >
            <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 150, lineHeight: 0.95, color: COLORS.red, letterSpacing: "-0.03em" }}>
              BLOCKED
            </div>
          </div>
          <div style={{ display: "flex", gap: 18, marginTop: 34, fontFamily: FONTS.mono, fontSize: 24 }}>
            <span style={{ color: COLORS.amber }}>ungrounded_file_path</span>
            <span style={{ color: COLORS.dim }}>·</span>
            <span style={{ color: COLORS.amber }}>ungrounded_sha</span>
          </div>
          <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 48, color: COLORS.text, marginTop: 26 }}>
            {ms}ms <span style={{ fontSize: 26, color: COLORS.dim, fontFamily: FONTS.mono }}>· no LLM call</span>
          </div>
        </AbsoluteFill>
      )}
    </AbsoluteFill>
  );
};

// ── 02 · STRAWMAN ─────────────────────────────────────────────────────────────
// embed → vector-search → prompt, + the three ways it breaks.
const STRAW_PIPE: PipeNode[] = [
  { label: "question", value: "Q", accent: "cyan" },
  { label: "embed", value: "vector", accent: "cyan" },
  { label: "search", value: "top-k", sub: "by meaning", accent: "cyan" },
  { label: "prompt", value: "LLM", accent: "cyan" },
];
export const Scene02Strawman: React.FC = () => {
  const f1 = useReveal(330, 360);
  const f2 = useReveal(510, 540);
  const f3 = useReveal(690, 720);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.strawman.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 90px", justifyContent: "flex-start", paddingTop: 150 }}>
        <Kicker text="naive rag · the strawman" color={COLORS.cyan} />
        <div style={{ width: 1640 }}>
          <PipelineGraph nodes={STRAW_PIPE} startFrame={8} stepFrames={20} nodeWidth={230} />
        </div>
        <div style={{ display: "flex", gap: 26, marginTop: 64 }}>
          <div style={{ opacity: f1 }}>
            <Panel accent="red" title="failure 1 · buried" width={470}>
              <div style={{ fontFamily: FONTS.body, fontSize: 23, lineHeight: 1.5, color: COLORS.text }}>
                Exact term <code style={{ color: COLORS.red, fontFamily: FONTS.mono }}>handleAuth()</code> ranks below things merely <i>related</i> to auth.
              </div>
            </Panel>
          </div>
          <div style={{ opacity: f2 }}>
            <Panel accent="red" title="failure 2 · severed" width={470}>
              <div style={{ fontFamily: FONTS.body, fontSize: 23, lineHeight: 1.5, color: COLORS.text }}>
                Character-split cuts a helper off from its component. The chunk loses its identity.
              </div>
            </Panel>
          </div>
          <div style={{ opacity: f3 }}>
            <Panel accent="red" title="failure 3 · fabricated" width={470}>
              <div style={{ fontFamily: FONTS.body, fontSize: 23, lineHeight: 1.5, color: COLORS.text }}>
                Weak retrieval → the model invents a plausible <code style={{ color: COLORS.red, fontFamily: FONTS.mono }}>filename</code> and answers anyway.
              </div>
            </Panel>
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ── 03 · LAYER 1 · AST STRUCTURAL CHUNKING ───────────────────────────────────
const CHUNK_CODE = `// adaptive-chunker.ts · CHUNKER_VERSION = 6
// tree-sitter walks the AST (13 language grammars)
function chunkComponent(node) {
  if (tooBig(node)) {
    // walk INTO the component for inner declarations
    for (const inner of innerDeclarations(node)) {
      const callers = reverseCallGraph.get(inner.name);
      emit({
        name: inner.name,
        // baked into the STORED text → enriches the embedding
        prefix: \`// [Parent: \${node.name}] \` +
                \`[Referenced by: \${callers.join(', ')}]\`,
      });
    }
  }
}`;
export const Scene03Ast: React.FC = () => {
  const before = useReveal(60, 90);
  const after = useReveal(120, 150);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.astChunking.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 96 }}>
        <LayerTag n={1} color={COLORS.green} />
        <Kicker text="ast structural chunking" color={COLORS.green} />
        <div style={{ display: "flex", gap: 34, alignItems: "flex-start" }}>
          <CodePanel
            code={CHUNK_CODE}
            language="tsx"
            filename="src/lib/rag/adaptive-chunker.ts"
            highlightLines={[8, 11, 12]}
            revealFrames={150}
            fontSize={21}
            width={1080}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 18, paddingTop: 8 }}>
            <div style={{ opacity: before }}>
              <Panel accent="red" title="before · char-split" width={420}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 19, lineHeight: 1.7, color: COLORS.dim }}>
                  <div>…ash) =&gt; &#123;</div>
                  <div style={{ color: COLORS.red }}>── cut at 2048 chars ──</div>
                  <div>const isRelated = (</div>
                  <div style={{ color: COLORS.red }}>orphan · no parent · no name</div>
                </div>
              </Panel>
            </div>
            <div style={{ opacity: after }}>
              <Panel accent="green" title="after · ast + call-graph" width={420}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 19, lineHeight: 1.7, color: COLORS.text }}>
                  <div style={{ color: COLORS.green }}>// [Parent: StackVisualizer]</div>
                  <div style={{ color: COLORS.green }}>// [Referenced by: card JSX]</div>
                  <div>function isRelated() &#123; … &#125;</div>
                  <div style={{ color: COLORS.dim }}>named · tied · discoverable</div>
                </div>
              </Panel>
            </div>
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ── 04 · LAYER 2 · MULTI-SIGNAL RRF RE-RANKING ───────────────────────────────
const RRF_SIGNALS: { label: string; note: string; color: string }[] = [
  { label: "hybrid (vec + bm25)", note: "0.7 / 0.3", color: COLORS.cyan },
  { label: "term density", note: "always", color: COLORS.green },
  { label: "import centrality", note: "arch / code", color: COLORS.lime },
  { label: "ui concept match", note: "conditional", color: COLORS.amber },
  { label: "chunk type", note: "overview", color: COLORS.violet },
];
export const Scene04Rrf: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.rrf.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 100 }}>
        <LayerTag n={2} color={COLORS.lime} />
        <Kicker text="reciprocal rank fusion · rrf_k = 60" color={COLORS.lime} />
        <div style={{ display: "flex", gap: 18, alignItems: "stretch", marginBottom: 30 }}>
          {RRF_SIGNALS.map((s, i) => {
            const op = interpolate(frame, [60 + i * 26, 90 + i * 26], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={s.label}
                style={{
                  opacity: op,
                  transform: `translateY(${(1 - op) * 16}px)`,
                  background: COLORS.panel,
                  border: `1.5px solid ${s.color}`,
                  borderRadius: 10,
                  padding: "16px 20px",
                  width: 268,
                  boxShadow: `0 0 26px -16px ${s.color}`,
                }}
              >
                <div style={{ fontFamily: FONTS.mono, fontSize: 14, letterSpacing: "0.14em", textTransform: "uppercase", color: COLORS.dim }}>
                  signal {i + 1}
                </div>
                <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 24, color: COLORS.text, marginTop: 6, lineHeight: 1.05 }}>
                  {s.label}
                </div>
                <div style={{ fontFamily: FONTS.mono, fontSize: 16, color: s.color, marginTop: 8 }}>
                  {s.note}
                </div>
              </div>
            );
          })}
        </div>
        <div
          style={{
            opacity: interpolate(frame, [240, 280], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
            fontFamily: FONTS.mono,
            fontSize: 30,
            color: COLORS.text,
            background: COLORS.panel2,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 10,
            padding: "16px 30px",
          }}
        >
          RRF<span style={{ color: COLORS.dim }}>(chunk) = </span>
          <span style={{ color: COLORS.lime }}>Σ 1 / (60 + rank</span>
          <span style={{ color: COLORS.dim }}>ᵢ)</span>
          <span style={{ color: COLORS.dim, fontSize: 22 }}>{"  "}— max per signal ≈ 0.0164, bounded</span>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ── 05 · LAYER 3 · QUERY-COMPLEXITY ROUTER ───────────────────────────────────
const TIERS: { name: string; spec: string; color: string }[] = [
  { name: "simple", spec: "1 pass · top-5 · no reasoning", color: COLORS.cyan },
  { name: "analytical", spec: "2 pass · top-15 · reason ×3", color: COLORS.green },
  { name: "comparative", spec: "2 pass · top-6 · synthesize", color: COLORS.lime },
  { name: "code", spec: "2 pass · top-15 · temp 0.2 · AST", color: COLORS.amber },
  { name: "multi_step", spec: "3 pass · top-20 · reason ×5", color: COLORS.magenta },
  { name: "agentic", spec: "ReAct · tool-managed passes", color: COLORS.violet },
  { name: "deep_research", spec: "per-subtopic · 16K budget", color: COLORS.cyan },
];
export const Scene05Router: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.router.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 110 }}>
        <LayerTag n={3} color={COLORS.amber} />
        <Kicker text="query router · 7 strategy tiers" color={COLORS.amber} />
        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: 1180 }}>
          {TIERS.map((t, i) => {
            const op = interpolate(frame, [50 + i * 22, 80 + i * 22], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={t.name}
                style={{
                  opacity: op,
                  transform: `translateX(${(1 - op) * -20}px)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: COLORS.panel,
                  border: `1px solid ${COLORS.line}`,
                  borderLeft: `4px solid ${t.color}`,
                  borderRadius: 8,
                  padding: "14px 24px",
                }}
              >
                <span style={{ fontFamily: FONTS.mono, fontSize: 26, color: t.color }}>{t.name}</span>
                <span style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim }}>{t.spec}</span>
              </div>
            );
          })}
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ── 06 · LAYER 4 · SEMANTIC CACHE ────────────────────────────────────────────
export const Scene06Cache: React.FC = () => {
  const frame = useCurrentFrame();
  const sim = interpolate(frame, [60, 120], [0.71, 0.984], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const hit = sim >= 0.97;
  const failClosed = useReveal(220, 260);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.cache.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 110 }}>
        <LayerTag n={4} color={COLORS.cyan} />
        <Kicker text="semantic cache · two-tier · cosine ≥ 0.97" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          <Panel accent={hit ? "green" : "amber"} title="query similarity" width={520}>
            <div style={{ ...center, height: 180 }}>
              <MetricCounter
                value={0.98}
                from={0.71}
                startFrame={60}
                durationFrames={60}
                accent={hit ? "green" : "amber"}
                decimals={2}
                fontSize={120}
              />
              <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: hit ? COLORS.green : COLORS.amber, marginTop: 8 }}>
                {hit ? "HIT · serve stored answer · skip LLM" : "cosine to nearest cached query"}
              </div>
            </div>
          </Panel>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Panel accent="cyan" title="redis · fast path" width={460}>
              <div style={{ fontFamily: FONTS.body, fontSize: 22, lineHeight: 1.45, color: COLORS.text }}>
                Upstash, ~ms reads. Postgres is the durable source-of-truth + invalidation store.
              </div>
            </Panel>
            <div style={{ opacity: failClosed }}>
              <Panel accent="amber" title="invalidation · fails closed" width={460}>
                <div style={{ fontFamily: FONTS.body, fontSize: 22, lineHeight: 1.45, color: COLORS.text }}>
                  Source re-sync → every touched row marked invalid. Missing signature = <b style={{ color: COLORS.amber }}>MISS</b>, never a stale hit.
                </div>
              </Panel>
            </div>
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ── 07 · LAYER 5 · SOURCE-MATURITY ALTITUDE GUARD ────────────────────────────
const SOURCES: { name: string; tier: string; caveat: string; color: string }[] = [
  { name: "competitor-demo-feasibility.md", tier: "COMPETITOR", caveat: "describes another product", color: COLORS.red },
  { name: "docs/roadmap/domain-packs.md", tier: "ROADMAP", caveat: "a future intention", color: COLORS.amber },
  { name: "specs/graph-pathfinding.md", tier: "status: planned", caveat: "not shipped", color: COLORS.amber },
  { name: "src/lib/rag/source-tier.ts", tier: "SHIPPED", caveat: "code = ground truth", color: COLORS.green },
];
export const Scene07Maturity: React.FC = () => {
  const frame = useCurrentFrame();
  const advisory = useReveal(180, 220);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.maturity.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 104 }}>
        <LayerTag n={5} color={COLORS.amber} />
        <Kicker text="source-maturity altitude guard" color={COLORS.amber} />
        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: 1180, marginBottom: 26 }}>
          {SOURCES.map((s, i) => {
            const op = interpolate(frame, [40 + i * 26, 70 + i * 26], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={s.name}
                style={{
                  opacity: op,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: COLORS.panel,
                  border: `1px solid ${COLORS.line}`,
                  borderLeft: `4px solid ${s.color}`,
                  borderRadius: 8,
                  padding: "13px 22px",
                }}
              >
                <span style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.text }}>{s.name}</span>
                <span style={{ display: "flex", gap: 14, alignItems: "baseline" }}>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 16, letterSpacing: "0.1em", color: s.color }}>{s.tier}</span>
                  <span style={{ fontFamily: FONTS.body, fontSize: 17, color: COLORS.dim }}>{s.caveat}</span>
                </span>
              </div>
            );
          })}
        </div>
        <div
          style={{
            opacity: advisory,
            fontFamily: FONTS.mono,
            fontSize: 22,
            color: COLORS.amber,
            background: COLORS.panel2,
            border: `1px solid ${COLORS.amber}55`,
            borderRadius: 10,
            padding: "16px 28px",
            maxWidth: 1180,
          }}
        >
          ⚠ SOURCE MATURITY ADVISORY → prepended to LLM context before it answers
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ── 08 · LAYER 6 · DETERMINISTIC OUTPUT GATE ─────────────────────────────────
const GATE_CODE = `// verify-output.ts · no LLM · < 150ms
// scan 2 — every path/SHA the text claims must have
// appeared in THIS run's tool history (agent_tool_calls)
const tools = await db.from('agent_tool_calls')
  .select('tool_name, input_digest, output_digest')
  .eq('run_id', ctx.runId);            // tenant-scoped

for (const path of claimedFilePaths(text))
  if (!toolHistoryMentions(tools, path))
    violations.push({ kind: 'ungrounded_file_path', excerpt: path });

for (const sha of claimedShas(text))
  if (!toolHistoryMentions(tools, sha))
    violations.push({ kind: 'ungrounded_sha', excerpt: sha });`;
export const Scene08Gate: React.FC = () => {
  const frame = useCurrentFrame();
  const scans = [
    { k: "banned_vocab", d: "blackboard · tool names · recency=day", c: COLORS.magenta },
    { k: "ungrounded_file_path / _sha", d: "cross-checked vs tool history", c: COLORS.red },
    { k: "numeric_grounding", d: "specific number, no evidence", c: COLORS.amber },
  ];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.outputGate.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 92 }}>
        <LayerTag n={6} color={COLORS.red} />
        <Kicker text="deterministic output gate · verify_output" color={COLORS.red} />
        <div style={{ display: "flex", gap: 34, alignItems: "flex-start" }}>
          <CodePanel
            code={GATE_CODE}
            language="tsx"
            filename="src/lib/agents/tools/verify-output.ts"
            highlightLines={[8, 9, 12, 13]}
            revealFrames={170}
            fontSize={20}
            width={1040}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 6 }}>
            {scans.map((s, i) => {
              const op = interpolate(frame, [200 + i * 50, 240 + i * 50], [0, 1], {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
              });
              return (
                <div key={s.k} style={{ opacity: op }}>
                  <Callout label={`scan ${i + 1} · ${s.k}`} accent="amber" width={440}>
                    <span style={{ color: COLORS.dim }}>{s.d}</span>
                  </Callout>
                </div>
              );
            })}
            <div style={{ opacity: useReveal(360, 400), marginTop: 8 }}>
              <Panel accent="green" title="returns" width={440}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.text }}>
                  &#123; ok: false, violations: [...] &#125;
                  <div style={{ color: COLORS.dim, fontSize: 16, marginTop: 6 }}>a list, not a rewrite</div>
                </div>
              </Panel>
            </div>
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ── 09 · PROOF OVER TIME · RECENCY EVAL PROBE ────────────────────────────────
export const Scene09Recency: React.FC = () => {
  const frame = useCurrentFrame();
  const swap = frame >= 200; // the probe's verdict flips the ranking
  const verdict = useReveal(240, 280);
  const fresh = { name: "loop_overview · Active Loops", meta: "mig 220 · 2026-05-28" };
  const stale = { name: "groupDocsByTheme · helper", meta: "committed 2026-05-05" };
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.recency.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 108 }}>
        <Kicker text="proof over time · recency-correctness probe" color={COLORS.violet} />
        <div style={{ display: "flex", gap: 26, alignItems: "stretch" }}>
          <Panel accent={swap ? "red" : "green"} title={swap ? "#2 · demoted" : "#1 · wrongly ranked"} width={540}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.text }}>{stale.name}</div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim, marginTop: 8 }}>{stale.meta}</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 19, color: swap ? COLORS.red : COLORS.dim, marginTop: 14 }}>
              {swap ? "stale distractor — must NOT out-rank fresh" : "fluent + cited → all graders passed it"}
            </div>
          </Panel>
          <Panel accent={swap ? "green" : "amber"} title={swap ? "#1 · promoted" : "#2 · genuinely newer"} width={540}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.text }}>{fresh.name}</div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim, marginTop: 8 }}>{fresh.meta}</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 19, color: swap ? COLORS.green : COLORS.dim, marginTop: 14 }}>
              {swap ? "fresh doc out-ranks stale → PASS" : "the answer should have surfaced this"}
            </div>
          </Panel>
        </div>
        <div
          style={{
            opacity: verdict,
            marginTop: 30,
            fontFamily: FONTS.mono,
            fontSize: 24,
            color: COLORS.violet,
            background: COLORS.panel2,
            border: `1px solid ${COLORS.violet}55`,
            borderRadius: 10,
            padding: "14px 28px",
          }}
        >
          evaluateRecencyProbe() · pure · no LLM · black-box over the ordered chunk list → survives a graph-backend swap
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ── 10 · THE FULL PIPELINE ───────────────────────────────────────────────────
const FULL_PIPE: PipeNode[] = [
  { label: "ingest", value: "AST", sub: "structural", accent: "green" },
  { label: "retrieve", value: "hybrid", sub: "0.7 / 0.3", accent: "cyan" },
  { label: "re-rank", value: "RRF", sub: "≤5 signals", accent: "lime" },
  { label: "route", value: "7 tiers", sub: "by complexity", accent: "amber" },
  { label: "cache", value: "≥0.97", sub: "fail-closed", accent: "cyan" },
  { label: "altitude", value: "maturity", sub: "advisory", accent: "amber" },
  { label: "gate", value: "verify", sub: "<150ms", accent: "red" },
];
export const Scene10Pipeline: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.pipeline.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 70px" }}>
      <Kicker text="grounded hybrid retrieval · defence in depth" color={COLORS.lime} />
      <div style={{ width: 1780 }}>
        <PipelineGraph nodes={FULL_PIPE} startFrame={10} stepFrames={26} nodeWidth={216} />
      </div>
      <div style={{ fontFamily: FONTS.body, fontSize: 28, color: COLORS.dim, marginTop: 56, textAlign: "center", maxWidth: 1400 }}>
        Pull any one layer out — a failure you already saw comes straight back.
      </div>
    </AbsoluteFill>
  </AbsoluteFill>
);

// ── 11 · CLOSE ───────────────────────────────────────────────────────────────
export const Scene11Close: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.close.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={74}>
          Most channels show the happy path.
          <br />
          <span style={{ color: COLORS.cyan }}>This is the scar tissue.</span>
        </Big>
        <div style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.dim, marginTop: 40, textAlign: "center", maxWidth: 1320 }}>
          Six layers, one eval gate — every number mapped to one real codebase.
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.22em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · open repo · <span style={{ color: COLORS.cyan }}>/admin/brain</span>
        </div>
      </AbsoluteFill>
    </SceneTransition>
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
